// web/src/ws.ts — WebSocket client with reconnect/backoff. OWNED BY S4 (S4 wires
// it into the console state). Skeleton implements the connection lifecycle +
// exponential backoff (RUBRIC R4.4); message dispatch is typed to RealtimeEvent.

import type { RealtimeEvent } from '../../shared/types';
import { ACT_AS_QUERY, getActAs } from './auth';

// WS origin resolution:
//  - explicit VITE_WS_BASE wins;
//  - else if VITE_API_BASE is set (prod), reuse it as ws(s)://… (the realtime DO
//    lives on the bg-api Worker);
//  - else fall back to the page origin so the Vite dev proxy forwards /ws.
function resolveWsBase(): string {
  if (import.meta.env.VITE_WS_BASE) return import.meta.env.VITE_WS_BASE;
  if (import.meta.env.VITE_API_BASE) {
    return import.meta.env.VITE_API_BASE.replace(/^http/, 'ws');
  }
  if (typeof location !== 'undefined' && location.host) {
    return location.protocol === 'https:' ? `wss://${location.host}` : `ws://${location.host}`;
  }
  return 'wss://api-bg.planesailing.io';
}

const WS_BASE: string = resolveWsBase();

type Listener = (event: RealtimeEvent) => void;

export class RealtimeClient {
  private ws: WebSocket | null = null;
  private listeners = new Set<Listener>();
  private backoffMs = 1000;
  private readonly maxBackoffMs = 30_000;
  private closedByUser = false;

  constructor(private path = '/ws') {}

  connect(): void {
    this.closedByUser = false;
    // The WS handshake can't carry headers, so the act-as identity rides as a
    // query param (?actAs=rhys|dana) — the API stub reads it for the principal.
    const sep = this.path.includes('?') ? '&' : '?';
    const url = `${WS_BASE}${this.path}${sep}${ACT_AS_QUERY}=${getActAs()}`;
    const ws = new WebSocket(url);
    this.ws = ws;

    ws.onopen = () => {
      this.backoffMs = 1000; // reset backoff on a healthy connection
    };
    ws.onmessage = (ev) => {
      try {
        const event = JSON.parse(ev.data as string) as RealtimeEvent;
        for (const l of this.listeners) l(event);
      } catch {
        // ignore malformed frames
      }
    };
    ws.onclose = () => {
      if (!this.closedByUser) this.scheduleReconnect();
    };
    ws.onerror = () => {
      ws.close();
    };
  }

  private scheduleReconnect(): void {
    const jitter = Math.random() * 250;
    setTimeout(() => this.connect(), this.backoffMs + jitter);
    this.backoffMs = Math.min(this.backoffMs * 2, this.maxBackoffMs);
  }

  on(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Tear down the current socket and reconnect (e.g. after an act-as switch,
   * so the handshake carries the new ?actAs identity). */
  reconnect(): void {
    this.backoffMs = 1000;
    this.ws?.close();
    this.ws = null;
    this.connect();
  }

  close(): void {
    this.closedByUser = true;
    this.ws?.close();
    this.ws = null;
  }
}
